import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCustomerHappinessRating } from "./enums/enumCustomerHappinessRating.generated";

// Model Objects

export class OutletCommunicationLogEntry extends ModelObject<OutletCommunicationLogEntry> {

    public id: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication subject.
    */
    public communicationSubjectId: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication type.
    */
    public communicationTypeId: Guid = Guid.createEmpty();
    /**
    * Rating of the customer happiness.
    */
    public customerHappinessRating: EnumCustomerHappinessRating = EnumCustomerHappinessRating.neutral;
    /**
    * Description of the communication log entry.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public description: string = "";
    /**
    * Outlet relating to which the communication occurred.
    */
    public outletId: Guid = Guid.createEmpty();
    /**
    * Timestamp at which the communication occurred.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    /**
    * User who created the communication log entry.
    */
    public userId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.communicationSubjectId = Guid.parseOrEmpty(jsonSerializedObject.communicationSubjectId);
          this.communicationTypeId = Guid.parseOrEmpty(jsonSerializedObject.communicationTypeId);
          this.customerHappinessRating = EnumCustomerHappinessRating.getByValue(jsonSerializedObject.customerHappinessRating, this.customerHappinessRating);
          this.description = jsonSerializedObject.description;
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.communicationSubjectId = this.communicationSubjectId.toString();
        toRet.communicationTypeId = this.communicationTypeId.toString();
        toRet.customerHappinessRating = this.customerHappinessRating.value;
        toRet.description = this.description;
        toRet.outletId = this.outletId.toString();
        toRet.timestamp = this.timestamp.toString();
        toRet.userId = this.userId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "OutletCommunicationLogEntry.Id cannot be empty"},
            {field: "communicationSubjectId", rule: (v: any) => !v.isEmpty() || "OutletCommunicationLogEntry.CommunicationSubjectId cannot be empty"},
            {field: "communicationTypeId", rule: (v: any) => !v.isEmpty() || "OutletCommunicationLogEntry.CommunicationTypeId cannot be empty"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OutletCommunicationLogEntry.OutletId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "OutletCommunicationLogEntry.UserId cannot be empty"},
    ];
}


export class OutletLogEntryFilter extends ModelObject<OutletLogEntryFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitOutletIds: readonly (Guid)[] | null = null;
    public get limitOutletIds(): readonly (Guid)[] | null { return this._limitOutletIds }
    public set limitOutletIds(value: readonly (Guid)[] | null) {
      this._limitOutletIds = value === undefined ? null : value;
    }
    private _limitUserIds: readonly (Guid)[] | null = null;
    public get limitUserIds(): readonly (Guid)[] | null { return this._limitUserIds }
    public set limitUserIds(value: readonly (Guid)[] | null) {
      this._limitUserIds = value === undefined ? null : value;
    }
    private _fromTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) timestamp.
    */
    public get fromTimestamp(): Instant | null { return this._fromTimestamp }
    public set fromTimestamp(value: Instant | null) {
      this._fromTimestamp = value === undefined ? null : value;
    }
    private _toTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) timestamp.
    */
    public get toTimestamp(): Instant | null { return this._toTimestamp }
    public set toTimestamp(value: Instant | null) {
      this._toTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitOutletIds = jsonSerializedObject.limitOutletIds == null ? null : jsonSerializedObject.limitOutletIds.map((v: any) => Guid.parseOrEmpty(v));
          this.limitUserIds = jsonSerializedObject.limitUserIds == null ? null : jsonSerializedObject.limitUserIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromTimestamp = jsonSerializedObject.fromTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromTimestamp);
          this.toTimestamp = jsonSerializedObject.toTimestamp == null ? null : Instant.parse(jsonSerializedObject.toTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitOutletIds = this.limitOutletIds == null ? null : this.limitOutletIds.map(v => v.toString());
        toRet.limitUserIds = this.limitUserIds == null ? null : this.limitUserIds.map(v => v.toString());
        toRet.fromTimestamp = this.fromTimestamp == null ? null : this.fromTimestamp.toString();
        toRet.toTimestamp = this.toTimestamp == null ? null : this.toTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredOutletLogEntryResults extends ModelObject<FilteredOutletLogEntryResults> {

    public matchingCount: number = 0;
    public outletLogEntries: (OutletCommunicationLogEntry)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.outletLogEntries = jsonSerializedObject.outletLogEntries.map((v: any) => new OutletCommunicationLogEntry(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.outletLogEntries = this.outletLogEntries.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "outletLogEntries", rule: (v: any) => !!v || "outletLogEntries is required"},
            {field: "outletLogEntries", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OutletCommunicationLogEntry[]).reduce((acc: Array<string>, curr: OutletCommunicationLogEntry) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SuggestedOutletCommunicationLogEntry extends ModelObject<SuggestedOutletCommunicationLogEntry> {

    /**
    * Id of the communication log's communication subject.
    */
    public communicationSubjectId: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication type.
    */
    public communicationTypeId: Guid = Guid.createEmpty();
    /**
    * Description of the communication log entry.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public description: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.communicationSubjectId = Guid.parseOrEmpty(jsonSerializedObject.communicationSubjectId);
          this.communicationTypeId = Guid.parseOrEmpty(jsonSerializedObject.communicationTypeId);
          this.description = jsonSerializedObject.description;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.communicationSubjectId = this.communicationSubjectId.toString();
        toRet.communicationTypeId = this.communicationTypeId.toString();
        toRet.description = this.description;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "communicationSubjectId", rule: (v: any) => !v.isEmpty() || "SuggestedOutletCommunicationLogEntry.CommunicationSubjectId cannot be empty"},
            {field: "communicationTypeId", rule: (v: any) => !v.isEmpty() || "SuggestedOutletCommunicationLogEntry.CommunicationTypeId cannot be empty"},
    ];
}


export class OutletCommunicationLogService extends ServiceBase {

  public async addOutletLogEntry(communicationSubjectId: Guid, communicationTypeId: Guid, customerHappinessRating: EnumCustomerHappinessRating, description: string, outletId: Guid, userId: Guid, timestampIfNotDbNow: Instant | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletCommunicationLogEntry> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectId:communicationSubjectId.toString(),
    communicationTypeId:communicationTypeId.toString(),
    customerHappinessRating:customerHappinessRating.value,
    description:description,
    outletId:outletId.toString(),
    userId:userId.toString(),
    timestampIfNotDbNow:timestampIfNotDbNow == null ? null : timestampIfNotDbNow.toString(),
    };
    const extractor = (response:any) => new OutletCommunicationLogEntry(response.outletLogEntry);;
    return this.makeJsonRequest("outletCommunicationLog/v1/addOutletLogEntry", req, true, extractor, opts);
  };

  public async getOutletLogEntry(outletLogEntryId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OutletCommunicationLogEntry> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletLogEntryId:outletLogEntryId.toString(),
    };
    const extractor = (response:any) => new OutletCommunicationLogEntry(response.outletLogEntry);;
    return this.makeJsonRequest("outletCommunicationLog/v1/getOutletLogEntry", req, false, extractor, opts);
  };

  public async getOutletLogEntries(outletLogEntryIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletCommunicationLogEntry)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletLogEntryIds:outletLogEntryIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.outletLogEntries.map((v: any) => new OutletCommunicationLogEntry(v));;
    return this.makeJsonRequest("outletCommunicationLog/v1/getOutletLogEntries", req, false, extractor, opts);
  };

  public async getOutletLogEntriesByFilter(filter: OutletLogEntryFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredOutletLogEntryResults> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredOutletLogEntryResults(response.results);;
    return this.makeJsonRequest("outletCommunicationLog/v1/getOutletLogEntriesByFilter", req, false, extractor, opts);
  };

  public async getTopTenLatestLogs(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SuggestedOutletCommunicationLogEntry)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.suggestedOutletCommunicationLogEntries.map((v: any) => new SuggestedOutletCommunicationLogEntry(v));;
    return this.makeJsonRequest("outletCommunicationLog/v1/getTopTenLatestLogs", req, false, extractor, opts);
  };
}

export const outletCommunicationLogService = new OutletCommunicationLogService();